import _ from 'lodash';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import * as actions from './RepaymentScheduleModal.actions';
import { selectAPI } from '../../../api/API.sagas';
import { OPEN_MODAL } from '../../../iwModals/Modals.actions';
import { fetchQueryClient } from '../../../iwQueryClient/iwQueryClient.sagas';
import { TrackRepaymentScheduleViewed } from '../../../tracking/amplitude.helpers';
import {
    getCBILSScheduledPayments,
    getSecondCBILSScheduledPayments
} from '../../CBILS/CBILSSelector';
import {
    getLatestFormalOfferFromAccounts,
    getOutstandingTotal,
    getScheduledPayments
} from '../../multiLoanDashboard/accountsSelector';

export const repaymentScheduleSagasWorkers = [
    fork(showRepaymentModal),
    fork(showAdHocAmountRepaymentModal),
    fork(showAccountsScheduledPaymentsModal),
    fork(showSecondCBILSAccountScheduledPaymentsModal)
];

function* showRepaymentModal() {
    yield takeLatest(
        actions.SHOW_REPAYMENT_SCHEDULE_MODAL,
        function* ({ isCBILS, customSchedule, source, accountId }) {
            yield trackRepaymentModalViewedEvent(source);
            const {
                credit_facility_repayment_schedule,
                draw_down_repayment_schedule
            } = yield selectAPI();

            //some parts of code is still using credit_facility_repayment_schedule, we need to phase it out.
            const apiSchedule = _.isEmpty(credit_facility_repayment_schedule)
                ? draw_down_repayment_schedule
                : credit_facility_repayment_schedule;

            yield put({
                type: actions.REPAYMENT_SCHEDULE_MODAL_DATA,
                schedule: customSchedule?.data || apiSchedule,
                isCBILS,
                accountId
            });

            yield put({ type: OPEN_MODAL, id: 'RepaymentScheduleModal' });
        }
    );
}

function* showAdHocAmountRepaymentModal() {
    yield takeLatest(
        actions.SHOW_AD_HOC_AMOUNT_REPAYMENT_SCHEDULE_MODAL,
        function* ({
            isCBILS,
            customSchedule,
            source,
            stateKey,
            drawdownAmount,
            offerId,
            productType
        }) {
            yield trackRepaymentModalViewedEvent(source);

            const response = yield call(
                fetchQueryClient({
                    queryKey: [
                        'draw_down_repayment_schedule',
                        stateKey,
                        drawdownAmount,
                        offerId,
                        productType
                    ]
                })
            );

            yield put({
                type: actions.AD_HOC_AMOUNT_REPAYMENT_SCHEDULE_MODAL_DATA,
                schedule: customSchedule?.data || response.data,
                isCBILS
            });

            yield put({ type: OPEN_MODAL, id: 'RepaymentScheduleModal' });
        }
    );
}

function* trackRepaymentModalViewedEvent(source) {
    const { productType, maxAmount, requestedAmount, outstandingBalance } =
        yield select((state) => {
            const latestFormalOffer = getLatestFormalOfferFromAccounts(state);
            const requestedProduct =
                state?.api?.application?.data?.requested_products?.[0];
            const productType = latestFormalOffer?.product_type;
            const outstandingBalance = getOutstandingTotal(state, productType);

            return {
                productType: productType,
                maxAmount: latestFormalOffer?.max_amount,
                requestedAmount: requestedProduct?.amount,
                outstandingBalance: outstandingBalance
            };
        });

    TrackRepaymentScheduleViewed({
        'CTA source': source,
        'Product type': productType,
        'Offered amount': maxAmount,
        'Requested amount': requestedAmount,
        'Outstanding balance': outstandingBalance || 0
    });
}

function* showAccountsScheduledPaymentsModal() {
    yield takeLatest(
        actions.SHOW_ACCOUNTS_SCHEDULED_REPAYMENTS_MODAL,
        function* ({ productType, accountId }) {
            const isCBILS = productType === 'CBILS';
            const schedule = isCBILS
                ? yield select(getCBILSScheduledPayments)
                : yield select((state) =>
                      getScheduledPayments(state, productType)
                  );

            yield put({
                type: actions.REPAYMENT_SCHEDULE_MODAL_DATA,
                schedule: schedule,
                accountId
            });

            yield put({ type: OPEN_MODAL, id: 'RepaymentScheduleModal' });
        }
    );
}

function* showSecondCBILSAccountScheduledPaymentsModal() {
    yield takeLatest(
        actions.SHOW_SECOND_CBILS_ACCOUNT_SCHEDULED_REPAYMENTS_MODAL,
        function* () {
            const schedule = yield select(getSecondCBILSScheduledPayments);

            yield put({
                type: actions.REPAYMENT_SCHEDULE_MODAL_DATA,
                schedule: schedule
            });

            yield put({ type: OPEN_MODAL, id: 'RepaymentScheduleModal' });
        }
    );
}
